import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Camera, Swords, Mic, Users, ShoppingBag, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { NAV_ITEMS } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import UserMenu from './nav/UserMenu';
import MobileNav from './nav/MobileNav';

const iconMap = { Home, Camera, Swords, Mic, Users, ShoppingBag };

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, setAuthOpen, setAuthMode, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const openLogin = () => { setAuthMode('login'); setAuthOpen(true); };
  const openRegister = () => { setAuthMode('register'); setAuthOpen(true); };
  const handleLogout = () => { logout(); toast.success('À bientôt ! 👋'); };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass border-b border-white/5' : 'bg-transparent'}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 flex items-center justify-center text-lg font-black text-white shadow-lg shadow-pink-500/40 group-hover:scale-105 transition-transform">M</div>
          <span className="text-xl font-black tracking-tight">
            <span className="text-white">MUZI</span>{' '}<span className="text-gradient">BATTLE</span>
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => {
            const Icon = iconMap[item.icon];
            const active = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link to={item.path} className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${active ? 'bg-white/8 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}>
                  <Icon size={16} />{item.name}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <UserMenu user={user} onLogout={handleLogout} />
          ) : (
            <>
              <button onClick={openLogin} className="text-zinc-300 hover:text-white text-sm font-medium px-3 py-2" data-testid="navbar-login-btn">Connexion</button>
              <Button onClick={openRegister} className="btn-neon text-white border-0 rounded-xl h-9 px-4 text-sm font-semibold" data-testid="navbar-register-btn">
                S&apos;inscrire
              </Button>
            </>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="md:hidden w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white" aria-label="Menu" data-testid="navbar-mobile-toggle">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <MobileNav
          navItems={NAV_ITEMS}
          iconMap={iconMap}
          location={location}
          user={user}
          onLogin={openLogin}
          onRegister={openRegister}
          onLogout={handleLogout}
        />
      )}
    </header>
  );
}
